// @flow
import * as React from 'react';
import {useEffect, useState} from 'react';

import {loadStripe} from '@stripe/stripe-js';
import {Elements, useStripe} from '@stripe/react-stripe-js';

import Box from 'sail/Box';
import Text from 'sail/Text/Text';
import {Body} from 'sail/Text';

import {DemoContainer, CardNumber} from 'docs/components';
import {defineMessages, FormattedMessage} from 'react-intl';
import {useLocation} from 'docs/data';
import './ConnectCheckoutDemo.scss';

type ConnectCheckoutDemoProps = {|
  type:
    | 'connect-destination-charge-checkout'
    | 'connect-direct-charge-checkout',
|};

const DEMO_ACCOUNT_KEY = 'pk_test_hyjpUhJneDCt1FZLtmN7HnkN00iZ1QDNpR';

const messages = defineMessages({
  checkout: {
    id: 'docsShell.ConnectCheckoutDemo.checkout',
    description: 'Button text for a checkout button',
    defaultMessage: 'Checkout',
  },
  success: {
    id: 'docsShell.ConnectCheckoutDemo.success',
    description:
      'After the user clicks a link and revists this docs page, we inform them with this message that their test purchase worked.',
    defaultMessage: 'Welcome back! You successfully completed a test purchase.',
  },
  canceled: {
    id: 'docsShell.ConnectCheckoutDemo.canceled',
    description:
      'After the user clicks a link to complete a test purchase and they cancel it, it takes them back to this page, and we inform them with this message that they canceled their test purchase.',
    defaultMessage: 'Welcome back! You canceled a test purchase.',
  },
  infoText: {
    id: 'docsShell.ConnectCheckoutDemo.infoText',
    description:
      'Text to inform the user that this demo is a sample integration',
    defaultMessage:
      'This sample integration is running in test mode, which means that it won’t create a real charge. Use {cardNumber} as your card number, any three-digit CVC code, and an expiration date in the future to simulate a successful payment.',
  },
});

const Demo = () => {
  const stripe = useStripe();
  const {location} = useLocation();

  // Note: This hardcodes the hash of the return url's
  // to be a section under 'web-accept-payment'. We can pass this is in as an
  // attribute if we need something more custom.
  const successUrl = `${location.baseUrl +
    location.pathname}?success=true#web-accept-payment`;
  const cancelUrl = `${location.baseUrl +
    location.pathname}?cancel=true#web-accept-payment`;

  const success = location.query.success === 'true';
  const canceled = location.query.cancel === 'true';

  const onClick = async () => {
    await stripe.redirectToCheckout({
      items: [{sku: 'sku_GiH3nMUcrP7SMD', quantity: 1}],
      successUrl,
      cancelUrl,
    });
  };

  return (
    <Box className="ConnectCheckoutDemo">
      <button
        className="ConnectCheckoutDemo-button"
        type="button"
        onClick={onClick}
      >
        Checkout
      </button>
      <Box margin={{vertical: 12}}>
        <Text color="dark" weight="medium" size={15} lineHeight={20}>
          {success && <FormattedMessage {...messages.success} />}
          {canceled && <FormattedMessage {...messages.canceled} />}
        </Text>
      </Box>
      <Body>
        <FormattedMessage
          {...messages.infoText}
          values={{
            cardNumber: <CardNumber number="4242 4242 4242 4242" copyable />,
          }}
        />
      </Body>
    </Box>
  );
};

export const ConnectCheckoutDemo = ({type}: ConnectCheckoutDemoProps) => {
  const [promise, setPromise] = useState(null);

  useEffect(() => {
    setPromise(loadStripe(DEMO_ACCOUNT_KEY));
  }, []);

  return (
    <Elements stripe={promise}>
      <DemoContainer demo={Demo} result={() => <></>} name={type} />
    </Elements>
  );
};
